/** 作品数据的创建、校验、迁移与序列化。
 *
 * 安全要求（规格书 5.3）：
 * - 导入时校验结构、数值范围与版本；
 * - 无效文件不破坏当前作品（调用方只在 ok 时替换）；
 * - 不执行导入内容中的脚本，只接受受控的剪口数据。
 */
import type {
  CutShape,
  FinishedSize,
  FoldId,
  GuideSettings,
  Project,
  SizeKind,
  Vec,
  ValidationIssue,
} from '../types';
import { CURRENT_FORMAT_VERSION } from '../types';
import { FOLD_IDS, getFold } from '../folds/definitions';
import { maxFinishedSize, paperPolygon } from '../folds/geometry';
import { PRINTABLE } from '../units';

export const MAX_CUTS = 2000;
export const MAX_POINTS_PER_CUT = 4000;
export const MIN_COORD_MM = -1000;
export const MAX_COORD_MM = 1000;

export const DEFAULT_GUIDES: GuideSettings = {
  showFoldLines: true,
  showGrid: false,
  showPaperEdge: true,
  snapMm: 1,
};

export const PAPER_COLORS = ['#d9483b', '#c2382f', '#e07b39', '#2f6f4e', '#1d4e89', '#4a3a8b', '#111111'] as const;

export function defaultSizeFor(foldId: FoldId): FinishedSize {
  const fold = getFold(foldId);
  if (fold.paper === 'square') {
    return { kind: 'diameter', value: [260] };
  }
  return { kind: 'widthHeight', value: [PRINTABLE.widthMm, PRINTABLE.heightMm] };
}

export function createDefaultProject(foldId: FoldId = 'radial30'): Project {
  return {
    formatVersion: CURRENT_FORMAT_VERSION,
    foldId,
    finishedSize: defaultSizeFor(foldId),
    paperColor: PAPER_COLORS[0],
    cuts: [],
    guides: { ...DEFAULT_GUIDES },
    updatedAt: new Date().toISOString(),
  };
}

const isFiniteNumber = (v: unknown): v is number => typeof v === 'number' && Number.isFinite(v);

function checkVec(p: unknown, where: string, issues: ValidationIssue[]): Vec | null {
  if (!p || typeof p !== 'object') {
    issues.push({ level: 'error', code: 'vec.shape', message: `${where}: 坐标不是对象` });
    return null;
  }
  const { x, y } = p as { x?: unknown; y?: unknown };
  if (!isFiniteNumber(x) || !isFiniteNumber(y)) {
    issues.push({ level: 'error', code: 'vec.number', message: `${where}: 坐标不是有限数字` });
    return null;
  }
  if (x < MIN_COORD_MM || x > MAX_COORD_MM || y < MIN_COORD_MM || y > MAX_COORD_MM) {
    issues.push({ level: 'error', code: 'vec.range', message: `${where}: 坐标超出允许范围` });
    return null;
  }
  return { x, y };
}

function normalizeCut(raw: unknown, index: number, issues: ValidationIssue[]): CutShape | null {
  const where = `剪口 #${index + 1}`;
  if (!raw || typeof raw !== 'object') {
    issues.push({ level: 'error', code: 'cut.shape', message: `${where}: 不是对象` });
    return null;
  }
  const c = raw as Record<string, unknown>;
  const id = typeof c.id === 'string' && c.id.length <= 64 ? c.id : `cut-${index}-${Math.random().toString(36).slice(2, 8)}`;
  const kind = c.kind;

  if (kind === 'polygon' || kind === 'freehand') {
    if (!Array.isArray(c.points)) {
      issues.push({ level: 'error', code: 'cut.points', message: `${where}: 缺少 points` });
      return null;
    }
    if (c.points.length < 3) {
      issues.push({ level: 'error', code: 'cut.points.tooFew', message: `${where}: 顶点少于 3 个` });
      return null;
    }
    if (c.points.length > MAX_POINTS_PER_CUT) {
      issues.push({ level: 'error', code: 'cut.points.tooMany', message: `${where}: 顶点超过 ${MAX_POINTS_PER_CUT}` });
      return null;
    }
    const points: Vec[] = [];
    for (let i = 0; i < c.points.length; i++) {
      const p = checkVec(c.points[i], `${where} 顶点 ${i + 1}`, issues);
      if (!p) return null;
      points.push(p);
    }
    return { id, kind, points };
  }

  if (kind === 'circle') {
    const center = checkVec(c.center, `${where} 圆心`, issues);
    if (!center || !isFiniteNumber(c.radius) || c.radius <= 0 || c.radius > MAX_COORD_MM) {
      issues.push({ level: 'error', code: 'cut.circle', message: `${where}: 圆参数无效` });
      return null;
    }
    return { id, kind: 'circle', center, radius: c.radius };
  }

  if (kind === 'triangle') {
    const center = checkVec(c.center, `${where} 中心`, issues);
    if (!center || !isFiniteNumber(c.radius) || c.radius <= 0 || !isFiniteNumber(c.rotation)) {
      issues.push({ level: 'error', code: 'cut.triangle', message: `${where}: 三角形参数无效` });
      return null;
    }
    return { id, kind: 'triangle', center, radius: c.radius, rotation: c.rotation };
  }

  if (kind === 'rect') {
    const center = checkVec(c.center, `${where} 中心`, issues);
    if (
      !center ||
      !isFiniteNumber(c.width) ||
      !isFiniteNumber(c.height) ||
      c.width <= 0 ||
      c.height <= 0 ||
      !isFiniteNumber(c.rotation)
    ) {
      issues.push({ level: 'error', code: 'cut.rect', message: `${where}: 矩形参数无效` });
      return null;
    }
    return { id, kind: 'rect', center, width: c.width, height: c.height, rotation: c.rotation };
  }

  issues.push({ level: 'error', code: 'cut.kind', message: `${where}: 未知的剪口类型 ${String(kind)}` });
  return null;
}

function normalizeSize(raw: unknown, foldId: FoldId, issues: ValidationIssue[]): FinishedSize {
  const fold = getFold(foldId);
  const max = maxFinishedSize(fold);
  const expectedKind: SizeKind = fold.paper === 'square' ? 'diameter' : 'widthHeight';
  const fallback = defaultSizeFor(foldId);
  if (!raw || typeof raw !== 'object') {
    issues.push({ level: 'warn', code: 'size.missing', message: '缺少成品尺寸，已使用默认值' });
    return fallback;
  }
  const s = raw as Record<string, unknown>;
  if (s.kind !== expectedKind) {
    issues.push({ level: 'warn', code: 'size.kind', message: `尺寸语义应为 ${expectedKind}，已按默认值处理` });
    return fallback;
  }
  if (!Array.isArray(s.value) || !s.value.every(isFiniteNumber)) {
    issues.push({ level: 'warn', code: 'size.value', message: '成品尺寸数值无效，已使用默认值' });
    return fallback;
  }
  const values = s.value as number[];
  if (expectedKind === 'diameter') {
    const d = Math.min(Math.max(values[0] ?? 0, 40), max.value[0]);
    if (d !== values[0]) {
      issues.push({ level: 'warn', code: 'size.clamp', message: `成品直径已限制为 ${d.toFixed(1)} mm（A4 可打印范围）` });
    }
    return { kind: 'diameter', value: [d] };
  }
  const w = Math.min(Math.max(values[0] ?? 0, 40), max.value[0]);
  const h = Math.min(Math.max(values[1] ?? w, 40), max.value[1]);
  if (w !== values[0] || h !== values[1]) {
    issues.push({ level: 'warn', code: 'size.clamp', message: `成品尺寸已限制为 ${w.toFixed(1)} × ${h.toFixed(1)} mm` });
  }
  return { kind: 'widthHeight', value: [w, h] };
}

export interface ValidationResult {
  ok: boolean;
  project: Project | null;
  issues: ValidationIssue[];
}

/** 校验任意输入（来自 JSON 文件或 localStorage），返回可安全使用的作品。 */
export function validateProject(raw: unknown): ValidationResult {
  const issues: ValidationIssue[] = [];
  if (!raw || typeof raw !== 'object') {
    return { ok: false, project: null, issues: [{ level: 'error', code: 'root', message: '文件内容不是对象' }] };
  }
  const p = raw as Record<string, unknown>;

  const version = p.formatVersion;
  if (!isFiniteNumber(version) || version < 1 || version > CURRENT_FORMAT_VERSION) {
    return {
      ok: false,
      project: null,
      issues: [
        {
          level: 'error',
          code: 'version',
          message: `不支持的作品格式版本：${String(version)}（当前支持 1–${CURRENT_FORMAT_VERSION}）`,
        },
      ],
    };
  }

  const foldId = p.foldId;
  if (typeof foldId !== 'string' || !(FOLD_IDS as string[]).includes(foldId)) {
    return {
      ok: false,
      project: null,
      issues: [{ level: 'error', code: 'foldId', message: `未知折法：${String(foldId)}` }],
    };
  }
  const fid = foldId as FoldId;

  if (!Array.isArray(p.cuts)) {
    return { ok: false, project: null, issues: [{ level: 'error', code: 'cuts', message: '缺少剪口列表' }] };
  }
  if (p.cuts.length > MAX_CUTS) {
    return {
      ok: false,
      project: null,
      issues: [{ level: 'error', code: 'cuts.tooMany', message: `剪口数量超过上限 ${MAX_CUTS}` }],
    };
  }

  const cuts: CutShape[] = [];
  for (let i = 0; i < p.cuts.length; i++) {
    const cut = normalizeCut(p.cuts[i], i, issues);
    if (cut) cuts.push(cut);
  }
  if (issues.some((i) => i.level === 'error')) {
    return { ok: false, project: null, issues };
  }

  const finishedSize = normalizeSize(p.finishedSize, fid, issues);

  const paperColor =
    typeof p.paperColor === 'string' && /^#[0-9a-fA-F]{6}$/.test(p.paperColor) ? p.paperColor : '#d9483b';

  const guides: GuideSettings = { ...DEFAULT_GUIDES };
  if (p.guides && typeof p.guides === 'object') {
    const g = p.guides as Record<string, unknown>;
    if (typeof g.showFoldLines === 'boolean') guides.showFoldLines = g.showFoldLines;
    if (typeof g.showGrid === 'boolean') guides.showGrid = g.showGrid;
    if (typeof g.showPaperEdge === 'boolean') guides.showPaperEdge = g.showPaperEdge;
    if (isFiniteNumber(g.snapMm) && g.snapMm >= 0 && g.snapMm <= 20) guides.snapMm = g.snapMm;
  }

  // 尺寸与剪口的可用性：纸张必须能生成
  const paper = paperPolygon(getFold(fid), finishedSize);
  if (paper.length < 3) {
    return { ok: false, project: null, issues: [...issues, { level: 'error', code: 'paper', message: '纸张几何无效' }] };
  }

  return {
    ok: true,
    project: {
      formatVersion: CURRENT_FORMAT_VERSION,
      foldId: fid,
      finishedSize,
      paperColor,
      cuts,
      guides,
      updatedAt: new Date().toISOString(),
    },
    issues,
  };
}

export function serializeProject(project: Project): string {
  return JSON.stringify({ ...project, updatedAt: new Date().toISOString() }, null, 2);
}

export function parseProject(text: string): ValidationResult {
  let raw: unknown;
  try {
    raw = JSON.parse(text);
  } catch {
    return {
      ok: false,
      project: null,
      issues: [{ level: 'error', code: 'json', message: '不是有效的 JSON 文件' }],
    };
  }
  return validateProject(raw);
}

/** 切换折法时的尺寸迁移：保持"同类语义"或退回到新折法的默认值。 */
export function migrateSizeForFold(project: Project, foldId: FoldId): FinishedSize {
  const fold = getFold(foldId);
  const current = project.finishedSize;
  if (fold.paper === 'square') {
    if (current.kind === 'diameter') return current;
    // 从矩形换算为外接直径（取短边）
    const short = Math.min(current.value[0] ?? 190, current.value[1] ?? 190);
    const max = maxFinishedSize(fold).value[0];
    return { kind: 'diameter', value: [Math.min(short * Math.SQRT2, max)] };
  }
  if (current.kind === 'widthHeight') return current;
  const side = (current.value[0] ?? 260) / Math.SQRT2;
  const w = Math.min(side, PRINTABLE.widthMm);
  const h = Math.min(side, PRINTABLE.heightMm);
  return { kind: 'widthHeight', value: [w, h] };
}